import React from 'react';

interface ModuleDatum {
  name: string;
  value: number;
  color: string;
}

const niceAxisMax = (maxPercent: number) => {
  if (maxPercent <= 0) return 40;
  return Math.max(40, Math.ceil(maxPercent / 10) * 10);
};

// Horizontal bar list with a percentage legend — replaces the old pie chart.
// A pie with several similarly-sized wedges makes magnitude comparison hard;
// sorted horizontal bars (module data already arrives sorted, see
// assignCategoricalColors) read the ranking at a glance instead.
const ModuleDistributionChart: React.FC<{ data: ModuleDatum[] }> = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const withPercent = data.map((d) => ({ ...d, percent: total > 0 ? (d.value / total) * 100 : 0 }));
  const axisMax = niceAxisMax(Math.max(...withPercent.map((d) => d.percent), 0));
  const ticks = Array.from({ length: axisMax / 10 + 1 }, (_, i) => i * 10);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 min-w-0">
        <div className="space-y-3">
          {withPercent.map((d, i) => (
            <div key={d.name} className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-4 text-right tabular-nums shrink-0">{withPercent.length - i}</span>
              <div className="relative flex-1 h-6">
                <div className="absolute inset-0" aria-hidden="true">
                  {ticks.map((t) => (
                    <div
                      key={t}
                      className="absolute top-0 bottom-0 w-px bg-gray-200"
                      style={{ left: `${(t / axisMax) * 100}%` }}
                    />
                  ))}
                </div>
                <div
                  className="absolute inset-y-0 left-0 rounded-r-[4px] flex items-center px-2 overflow-hidden"
                  style={{ width: `${Math.max((d.percent / axisMax) * 100, 2)}%`, backgroundColor: d.color }}
                >
                  <span className="text-xs font-semibold text-white truncate">{d.name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <span className="w-4 shrink-0" />
          <div className="relative flex-1 h-4">
            {ticks.map((t) => (
              <span
                key={t}
                className="absolute text-[11px] text-gray-400 -translate-x-1/2 first:translate-x-0 last:-translate-x-full"
                style={{ left: `${(t / axisMax) * 100}%` }}
              >
                {t}%
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-4 content-start lg:w-48 shrink-0">
        {withPercent.map((d) => (
          <div key={d.name}>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
              <span className="text-xs text-gray-500 truncate">{d.name}</span>
            </div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{Math.round(d.percent)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModuleDistributionChart;
